import { ETOILES_TROP_DURES_FATIGUE } from '@nextilt/commun';
import type { CourageDeMaintenant, DossierDe, PossibleDuJour } from '../../partage/api/types';
import { etoilesEnMots, MOT_NE_RENTRE_PLUS, MOTS_CRANS_DE_TEMPS } from '../../partage/vocabulaire';
import { chercher } from './journeeEnCours';
import { ceQuEstLaTache, debloquantDe } from './propositionDuJour';

/**
 * Les règles sans rendu de « Je choisis moi-même », au téléphone (chantier
 * `telephone-journee-agenda`, ticket 03, décisions d'Igor du 2026-10-05, 10 et 20) : trois boutons,
 * le projet, le temps, le courage, et la liste des possibles du jour qui leur répond, le débloquant
 * en tête. Repris par « + Ajouter une tâche » de « Propose-moi une journée ».
 *
 * Le serveur range les possibles dans l'ordre de la flottaison, au courage demandé : changer le
 * courage relit la liste à ce courage. Ici, seulement filtrer, compter et mettre le débloquant en
 * tête, sans rien reclasser d'autre.
 */

/** Les crans du temps : jusqu'à 10 min, 30 min, 1 h, plus d'une heure, tout. */
export type CranDeTemps = keyof typeof MOTS_CRANS_DE_TEMPS;
export const CRANS_DE_TEMPS: readonly CranDeTemps[] = ['dix_min', 'trente_min', 'une_heure', 'plus_long', 'tout'];
const MINUTES_DU_CRAN: Record<Exclude<CranDeTemps, 'plus_long' | 'tout'>, number> = { dix_min: 10, trente_min: 30, une_heure: 60 };

/** Les trois boutons. `projet` : l'identifiant du projet, `''` pour les tâches sans projet, `null` pour tous. */
export interface FiltresDuJour {
  projet: string | null;
  temps: CranDeTemps;
  courage: CourageDeMaintenant;
}

/** Au départ : tous les projets, toute durée, et le courage répondu à la question. */
export function filtresDeDepart(courage: CourageDeMaintenant): FiltresDuJour {
  return { projet: null, temps: 'tout', courage };
}

/** Une durée passe le cran du temps : elle y tient ; « Plus long », plus d'une heure. */
export function passeLeTemps(minutes: number, cran: CranDeTemps): boolean {
  if (cran === 'tout') return true;
  if (cran === 'plus_long') return minutes > MINUTES_DU_CRAN.une_heure;
  return minutes <= MINUTES_DU_CRAN[cran];
}

/**
 * Le courage : « Fatigué » écarte le difficile, la règle du moteur pour le débloquant ; une
 * difficulté non réglée reste. Les autres courages n'écartent rien, ils changent l'ordre (le serveur).
 */
export function passeLeCourage(charge: number | null, courage: CourageDeMaintenant): boolean {
  return !(courage === 'fatigue' && charge !== null && charge >= ETOILES_TROP_DURES_FATIGUE);
}

/** La clé du projet d'une tâche : son identifiant, `''` sans projet. */
export function projetDe(p: Pick<PossibleDuJour, 'dossier'>): string {
  return p.dossier?.id ?? '';
}

/** Une tâche de la liste : tient-elle dans ce qui reste, est-elle le débloquant. */
export interface TacheAuChoix {
  tache: PossibleDuJour;
  tient: boolean;
  debloquant: boolean;
}

const passeLeTempsEtLeCourage = (p: PossibleDuJour, f: FiltresDuJour) => passeLeTemps(p.minutes, f.temps) && passeLeCourage(p.charge, f.courage);

/**
 * La liste qui répond aux trois boutons, sans ce qui est déjà dans la journée : le débloquant
 * (celle qui libère le plus, la première à égalité) en tête, les autres dans l'ordre du serveur.
 */
export function tachesAuChoix(possibles: readonly PossibleDuJour[], f: FiltresDuJour, pris: readonly string[], reste: number): TacheAuChoix[] {
  const dejaLa = new Set(pris);
  const gardees = possibles.filter((p) => !dejaLa.has(p.id) && (f.projet === null || projetDe(p) === f.projet) && passeLeTempsEtLeCourage(p, f));
  const debloquant = debloquantDe(gardees);
  const enTete = gardees.filter((p) => p.id === debloquant);
  return [...enTete, ...gardees.filter((p) => p.id !== debloquant)].map((p) => ({ tache: p, tient: p.minutes <= reste, debloquant: p.id === debloquant }));
}

/** Un projet de la liste du bouton : combien de ses tâches répondent au temps et au courage. */
export interface ProjetAuChoix {
  cle: string;
  dossier: DossierDe;
  combien: number;
}

/**
 * Les projets qui ont encore une tâche hors de la journée, dans l'ordre où le premier vient dans la
 * liste du serveur ; chacun compte ce qui répond aux deux autres boutons (le temps, le courage).
 */
export function projetsAuChoix(possibles: readonly PossibleDuJour[], f: FiltresDuJour, pris: readonly string[]): ProjetAuChoix[] {
  const dejaLa = new Set(pris);
  const projets = new Map<string, ProjetAuChoix>();
  for (const p of possibles) {
    if (dejaLa.has(p.id)) continue;
    const cle = projetDe(p);
    const projet = projets.get(cle) ?? { cle, dossier: p.dossier, combien: 0 };
    if (passeLeTempsEtLeCourage(p, f)) projet.combien += 1;
    projets.set(cle, projet);
  }
  return [...projets.values()];
}

/** « Chercher une autre tâche » : par le titre, dans tous les possibles, sans les trois boutons. */
export function tachesCherchees(possibles: readonly PossibleDuJour[], texte: string, pris: readonly string[], reste: number): { tache: PossibleDuJour; tient: boolean }[] {
  const dejaLa = new Set(pris);
  return chercher(possibles, texte)
    .filter((p) => !dejaLa.has(p.id))
    .map((p) => ({ tache: p, tient: p.minutes <= reste }));
}

/**
 * Ce que dit une ligne de la liste : son projet quand tous sont montrés, sa durée, ce qu'elle est
 * (débloquant, ce qu'elle libère, son échéance), sa difficulté, et « ne rentre plus ».
 */
export function ligneAuChoix(t: Pick<TacheAuChoix, 'tache' | 'tient' | 'debloquant'>, avecProjet: boolean): string {
  return [
    avecProjet ? t.tache.dossier?.titre : null,
    ceQuEstLaTache(t.tache, t.debloquant ? t.tache.id : null),
    t.tache.charge ? etoilesEnMots(t.tache.charge) : null,
    t.tient ? null : MOT_NE_RENTRE_PLUS,
  ]
    .filter(Boolean)
    .join(' · ');
}
